import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import { useTheme } from 'styled-components';
import { Icon } from 'Components/UI/Icon/Icon';
import { IconName } from 'Components/UI/Icon/IconClasses';
import {
  BimMeasureMode,
  BimMeasurement,
  BimProjectionMode,
  BimSectionPlane,
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { BimSectionPanel } from './BimSectionPanel';
import {
  ToolbarContainer, Pill, PillRow, ToolbarButton, Divider, ToolAnchor, GroupPopover, MainPillAnchor, ProgressSlot,
} from './styles';

type ToolGroup = 'frame' | 'measure' | 'projection';

interface IToolProps {
  icon: IconName;
  iconSize?: number;
  label: string;
  disabled?: boolean;
  active?: boolean;
  /** Botão que abre um grupo: leva o chevron e esconde o tooltip com o grupo aberto. */
  groupOpen?: boolean;
  /** Painel aberto sem ser grupo (o corte): mesma borda, sem o chevron. */
  open?: boolean;
  onClick(): void;
}

const Tool: FC<IToolProps> = ({ icon, iconSize, label, disabled, active, groupOpen, open, onClick }) => {
  const theme = useTheme();
  const color = active ? theme.colors.primary.primary : theme.colors.surface.onSurfaceVariant;
  const isGroup = groupOpen !== undefined;
  const isOpen = !!groupOpen || !!open;

  return (
    // Aberto, o tooltip cobriria os botões do próprio grupo.
    <Tooltip title={label} placement="top" open={isOpen ? false : undefined}>
      <ToolbarButton
        type="button"
        aria-label={label}
        $active={!!active}
        $group={isGroup}
        $open={isOpen}
        disabled={disabled}
        onClick={() => !disabled && onClick()}
      >
        <Icon icon={icon} customSize={iconSize || 18} customColor={color} />
        {isGroup && <UpOutlined style={{ fontSize: 10, color }} />}
      </ToolbarButton>
    </Tooltip>
  );
};

// Só `enabled`/`onFit` são obrigatórios: cada ferramenta aparece quando o seu
// handler é passado, o que deixa o federado usar a MESMA toolbar.
interface IBim3DToolbarProps {
  enabled: boolean;
  onFit(): void;
  hasSelection?: boolean;
  /** Seleção: o mesmo que o Esc na cena, sem apagar as medições. */
  onResetView?(): void;
  sectionOpen?: boolean;
  section?: BimSectionPlane;
  onFitSelection?(): void;
  onToggleSection?(): void;
  onSectionChange?(next: Partial<BimSectionPlane>): void;
  measureMode?: BimMeasureMode;
  measurements?: BimMeasurement[];
  onToggleMeasure?(mode: Exclude<BimMeasureMode, 'none'>): void;
  onClearMeasurements?(): void;
  projection?: BimProjectionMode;
  onProjectionChange?(mode: BimProjectionMode): void;
  /** Alterna o fundo claro/escuro — pílula própria no fim, como o `switchbg` do 2D. */
  onToggleBackground?(): void;
  /** Indicador ancorado acima e à esquerda da barra (ex.: progresso do data model). */
  progress?: ReactNode;
}

/**
 * Barra de ferramentas do viewer 3D: seleção, grupos que abrem acima do próprio
 * botão (enquadrar · câmera · medição) e o plano de corte.
 *
 * Orbit/pan/zoom não são botões: já são gestos de mouse do motor. Árvore,
 * propriedades e visibilidade ficam nos drawers.
 */
export const Bim3DToolbar: FC<IBim3DToolbarProps> = ({
  enabled,
  hasSelection,
  onResetView,
  sectionOpen,
  section,
  onFit,
  onFitSelection,
  onToggleSection,
  onSectionChange,
  measureMode = 'none',
  measurements = [],
  onToggleMeasure,
  onClearMeasurements,
  projection = 'perspective',
  onProjectionChange,
  onToggleBackground,
  progress,
}) => {
  const { t } = useTranslation('viewer');
  const [openGroup, setOpenGroup] = useState<ToolGroup | null>(null);

  const exitMeasure = () => {
    if (measureMode !== 'none') { onToggleMeasure?.(measureMode); }
  };

  // Fechar a medição (direto ou abrindo outro grupo) sai da ferramenta: deixar o
  // modo ligado sem os botões à vista faria o clique no 3D virar ponto de medida
  // sem nada indicando isso.
  const closeGroup = () => {
    if (openGroup === 'measure') { exitMeasure(); }
    setOpenGroup(null);
  };

  // Grupos e painel de corte são exclusivos: abrir um fecha o outro.
  const toggleGroup = (group: ToolGroup) => {
    if (openGroup === group) { closeGroup(); return; }
    if (openGroup === 'measure') { exitMeasure(); }
    if (sectionOpen) { onToggleSection?.(); }
    setOpenGroup(group);
  };

  const toggleSection = () => {
    if (!sectionOpen) { closeGroup(); }
    onToggleSection?.();
  };

  const resetView = () => {
    exitMeasure();
    setOpenGroup(null);
    onResetView?.();
  };

  return (
    <ToolbarContainer>
      {/* O corte é painel, não submenu: fica no meio, acima da barra. */}
      {sectionOpen && section && onSectionChange && (
        <BimSectionPanel section={section} onChange={onSectionChange} />
      )}

      <PillRow>
      <MainPillAnchor>
      {progress && <ProgressSlot>{progress}</ProgressSlot>}
      <Pill>
        {onResetView && (
          <Tool
            icon="pointStyle"
            label={t('bim3dToolbar.select')}
            disabled={!enabled}
            active={measureMode === 'none' && openGroup === null}
            onClick={resetView}
          />
        )}

        <ToolAnchor>
          {openGroup === 'frame' && (
            <GroupPopover>
              {onFitSelection && (
                <Tool
                  icon="enquadrarSelecao"
                  label={t('bim3dToolbar.fitSelection')}
                  disabled={!enabled || !hasSelection}
                  onClick={onFitSelection}
                />
              )}
              <Tool
                icon="enquadrarModelo"
                label={t('bim3dToolbar.fit')}
                disabled={!enabled}
                onClick={onFit}
              />
            </GroupPopover>
          )}
          <Tool
            icon="buscar"
            iconSize={16}
            label={t('bim3dToolbar.frameGroup')}
            disabled={!enabled}
            groupOpen={openGroup === 'frame'}
            onClick={() => toggleGroup('frame')}
          />
        </ToolAnchor>

        {onProjectionChange && (
          <ToolAnchor>
            {openGroup === 'projection' && (
              <GroupPopover>
                <Tool
                  icon="ortogonal"
                  label={t('bim3dToolbar.orthographic')}
                  disabled={!enabled}
                  active={projection === 'orthographic'}
                  onClick={() => onProjectionChange('orthographic')}
                />
                <Tool
                  icon="perspectiva"
                  label={t('bim3dToolbar.perspective')}
                  disabled={!enabled}
                  active={projection === 'perspective'}
                  onClick={() => onProjectionChange('perspective')}
                />
              </GroupPopover>
            )}
            <Tool
              icon="camera"
              label={t('bim3dToolbar.projectionGroup')}
              disabled={!enabled}
              groupOpen={openGroup === 'projection'}
              onClick={() => toggleGroup('projection')}
            />
          </ToolAnchor>
        )}

        {onToggleMeasure && (
          <ToolAnchor>
            {openGroup === 'measure' && (
              <GroupPopover>
                <Tool
                  icon="distance"
                  label={t('bim3dToolbar.measureDistance')}
                  disabled={!enabled}
                  active={measureMode === 'distance'}
                  onClick={() => onToggleMeasure('distance')}
                />
                <Tool
                  icon="area"
                  label={t('bim3dToolbar.measureArea')}
                  disabled={!enabled}
                  active={measureMode === 'area'}
                  onClick={() => onToggleMeasure('area')}
                />
                <Divider />
                <Tool
                  icon="eraser"
                  label={t('bim3dToolbar.measureClear')}
                  disabled={!enabled || measurements.length === 0}
                  onClick={() => onClearMeasurements?.()}
                />
              </GroupPopover>
            )}
            <Tool
              icon="medicao"
              label={t('bim3dToolbar.measure')}
              disabled={!enabled}
              groupOpen={openGroup === 'measure'}
              onClick={() => toggleGroup('measure')}
            />
          </ToolAnchor>
        )}

        {onToggleSection && (
          <Tool
            icon="section"
            iconSize={20}
            label={t('bim3dToolbar.section')}
            disabled={!enabled}
            open={sectionOpen}
            onClick={toggleSection}
          />
        )}
      </Pill>
      </MainPillAnchor>

      {onToggleBackground && (
        <Pill>
          <Tool
            icon="switchBg"
            label={t('bim3dToolbar.switchBackground')}
            disabled={!enabled}
            onClick={onToggleBackground}
          />
        </Pill>
      )}
      </PillRow>
    </ToolbarContainer>
  );
};
